import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import type { ContractListItem } from '../contractTypes';
import { ContractListApiFactory } from '../api/contractListApi';

interface UseContractsByEmployeeResult {
  data: ContractListItem[];
  loading: boolean;
  error: ApiError | Error | null;
  retry: () => void;
}

/**
 * Fetches the full contract history list for a given employee.
 * Each item is a ContractListItem summary suitable for the history table.
 */
export function useContractsByEmployee(
  employeeId: string,
  lang: string,
): UseContractsByEmployeeResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => ContractListApiFactory(client, lang), [client, lang]);

  const [data, setData] = useState<ContractListItem[]>([]);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const retry = useCallback(() => setRetryKey(k => k + 1), []);

  useEffect(() => {
    if (!employeeId) return;

    const ctrl = new AbortController();
    setData([]);
    setError(null);

    api
      .getAllByEmployeeId(employeeId, { signal: ctrl.signal })
      .then(contracts => setData(contracts))
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setData([]);
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => ctrl.abort();
  }, [api, employeeId, retryKey]);

  return { data, loading, error, retry };
}
